import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import Seo from "./components/Seo";

import LoginPage from "./auth/pages/LoginPage";
import ResetPasswordPage from "./auth/pages/ResetPasswordPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import FeaturesPage from "./public/pages/FeaturesPage";
import SongRequestsLandingPage from "./public/pages/SongRequestsLandingPage";
import SetlistAppLandingPage from "./public/pages/SetlistAppLandingPage";
import OrderExtrasPage from "./public/pages/OrderExtrasPage";
import WaitlistPage from "./public/pages/WaitlistPage";
import SongRequestsPage from "./admin/pages/SongRequestsPage";
import SettingsPage from "./admin/pages/SettingsPage";
import SubscriptionPage from "./admin/pages/SubscriptionPage";
import AdminFaqPage from "./admin/pages/AdminFaqPage";
import Navbar from "./admin/components/Navbar";
import PublicArtistPage from "./public/pages/PublicArtistPage";
import ParanoidiFooter from "./shared/components/ParanoidiFooter.jsx";
import ParanoidiAdminFooter from "./admin/components/ParanoidiAdminFooter";
import ScrollToTopButton from "./shared/components/ScrollToTopButton";
import { ThemeProvider } from "./admin/context/ThemeContext";
import { initAnalytics, trackEvent, trackPageView } from "./lib/analytics";
import CookieConsent from "./shared/components/CookieConsent";

/*
  Turvallinen oletus:
  Jos esiintyjän tietoja ei ole vielä sessionStoragessa,
  oletetaan Basic, jolloin maksullisia ominaisuuksia ei näytetä vahingossa.
*/
const BASIC_FEATURES = {
  name: "Basic",
  monthlyPriceEur: 9.9,
  songLimit: 100,
  setlistsEnabled: false,
  savedSetlistsEnabled: false,
  requestStatsEnabled: false,
};

/*
  Appin peruskonfiguraatio.
  Esiintyjäkohtaiset ominaisuudet lisätään tämän päälle App-komponentissa.
*/
const baseConfig = {
  enabled: true,
  theme: {
    defaultTheme: "dark",
    storageKey: "paranoidi_theme",
  },
  features: BASIC_FEATURES,
};

/*
  Luetaan kirjautunut esiintyjä sessionStoragesta.
  Jos data on rikki tai puuttuu, palautetaan null.
*/
function getStoredValue(key) {
  const sessionValue = window.sessionStorage.getItem(key);

  if (sessionValue) {
    return sessionValue;
  }

  const localValue = window.localStorage.getItem(key);

  if (localValue) {
    window.sessionStorage.setItem(key, localValue);
    return localValue;
  }

  return null;
}

function setStoredValue(key, value) {
  if (!value) {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
    return;
  }

  window.sessionStorage.setItem(key, value);
  window.localStorage.setItem(key, value);
}

function getStoredPerformer() {
  try {
    const rawPerformer = getStoredValue("paranoidi_auth_performer");
    return rawPerformer ? JSON.parse(rawPerformer) : null;
  } catch {
    return null;
  }
}

function getApiBaseUrl() {
  const fromWindow =
    typeof window !== "undefined"
      ? window.PARANOIDI_API_BASE_URL
      : "";

  if (fromWindow) {
    return String(fromWindow).replace(/\/+$/, "");
  }

  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:4000/api";
  }

  return "/backend/api";
}

function clearStoredAuthState() {
  [
    "paranoidi_logged_in",
    "paranoidi_auth_user",
    "paranoidi_auth_performer",
    "paranoidi_performer_slug",
    "paranoidi_auth_type",
  ].forEach((key) => {
    setStoredValue(key, "");
  });
}

function getCurrentPathname() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function NotFoundPage() {
  const { t, i18n } = useTranslation("admin");
  const isEnglish = i18n.resolvedLanguage?.toLowerCase().startsWith("en");

  return (
    <main className="min-h-screen bg-black px-4 py-16 text-white sm:px-6">
      <section className="mx-auto flex min-h-[70vh] max-w-3xl items-center justify-center">
        <div className="w-full rounded-[2rem] border border-white/10 bg-zinc-950/90 p-8 text-center shadow-2xl shadow-black/40 md:p-10">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300">
            Paranoidi
          </p>

          <h1 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
            {t("app.notFound.title")}
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-zinc-300 md:text-lg">
            {t("app.notFound.description")}
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={isEnglish ? "/en/" : "/"}
              className="inline-flex rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400"
            >
              {t("app.notFound.backHome")}
            </a>

            <a
              href={isEnglish ? "/en/features" : "/ominaisuudet"}
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:border-white/30 hover:bg-white/10"
            >
              {t("app.notFound.features")}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const { t, i18n } = useTranslation("admin");
  const [pathname, setPathname] = useState(getCurrentPathname);

  useEffect(() => {
    function handlePathChange() {
      setPathname(getCurrentPathname());
    }

    window.addEventListener("popstate", handlePathChange);
    window.addEventListener("paranoidi:navigate", handlePathChange);

    return () => {
      window.removeEventListener("popstate", handlePathChange);
      window.removeEventListener("paranoidi:navigate", handlePathChange);
    };
  }, []);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  const isAdminRoute =
    pathname === "/admin" ||
    pathname === "/admin/settings" ||
    pathname === "/admin/subscription" ||
    pathname === "/admin/faq";

  const isEnglishHomeRoute = pathname === "/en";
  const isEnglishFeaturesRoute = pathname === "/en/features";
  const isEnglishSongRequestsLandingRoute = pathname === "/en/song-requests";
  const isEnglishSetlistAppRoute = pathname === "/en/setlist-app-for-musicians";
  const isEnglishPrivacyRoute = pathname === "/en/privacy";
  const isEnglishTermsRoute = pathname === "/en/terms";

  const isHomeRoute = pathname === "/" || isEnglishHomeRoute;
  const isLoginRoute = pathname === "/login";
  const isResetPasswordRoute =
    pathname === "/reset-password" || pathname.startsWith("/reset-password/");
  const isContactRoute = pathname === "/yhteys";
  const isPrivacyRoute = pathname === "/tietosuoja" || isEnglishPrivacyRoute;
  const isTermsRoute = pathname === "/kayttoehdot" || isEnglishTermsRoute;
  const isFeaturesRoute = pathname === "/ominaisuudet" || isEnglishFeaturesRoute;
  const isSongRequestsLandingRoute =
    pathname === "/biisitoiveet-keikalle" ||
    isEnglishSongRequestsLandingRoute;
  const isOrderExtrasRoute = pathname === "/taydenna-tilaustasi";

  const isEnglishSeoRoute =
    isEnglishHomeRoute ||
    isEnglishFeaturesRoute ||
    isEnglishSongRequestsLandingRoute ||
    isEnglishSetlistAppRoute ||
    isEnglishPrivacyRoute ||
    isEnglishTermsRoute;

  const isFinnishSeoRoute =
    pathname === "/" ||
    pathname === "/ominaisuudet" ||
    pathname === "/biisitoiveet-keikalle" ||
    pathname === "/tietosuoja" ||
    pathname === "/kayttoehdot";

  useEffect(() => {
    const routeLanguage = isEnglishSeoRoute
      ? "en"
      : isFinnishSeoRoute
        ? "fi"
        : null;

    if (!routeLanguage) {
      return;
    }

    try {
      window.localStorage.setItem("paranoidi_ui_language", routeLanguage);
    } catch {
      // Kielireitti toimii myös ilman localStoragea.
    }

    window.PARANOIDI_INITIAL_UI_LANGUAGE = routeLanguage;
    document.documentElement.lang = routeLanguage;
    document.documentElement.dir = "ltr";

    const currentLanguage = String(
      i18n.resolvedLanguage || i18n.language || ""
    )
      .trim()
      .toLowerCase()
      .replace(/_/g, "-")
      .split("-")[0];

    if (currentLanguage !== routeLanguage) {
      i18n.changeLanguage(routeLanguage);
    }
  }, [i18n, isEnglishSeoRoute, isFinnishSeoRoute]);

  const isAdminSettingsRoute = pathname === "/admin/settings";
  const isAdminSubscriptionRoute = pathname === "/admin/subscription";
  const isAdminFaqRoute = pathname === "/admin/faq";

  const isPublicArtistRoute = pathname.startsWith("/p/");
  const publicPathParts = isPublicArtistRoute
    ? pathname.replace(/^\/p\//, "").split("/")
    : [];

  const artistSlug = publicPathParts[0] || "";

  const seoProps = isHomeRoute
    ? {
        title: t("app.seo.home.title"),
        description: t("app.seo.home.description"),
        path: isEnglishHomeRoute ? "/en/" : "/",
      }
    : isFeaturesRoute
      ? {
          title: t("app.seo.features.title"),
          description: t("app.seo.features.description"),
          path: isEnglishFeaturesRoute ? "/en/features" : "/ominaisuudet",
        }
      : isSongRequestsLandingRoute
        ? {
            title: t("app.seo.songRequestsLanding.title"),
            description: t("app.seo.songRequestsLanding.description"),
            path: isEnglishSongRequestsLandingRoute
              ? "/en/song-requests"
              : "/biisitoiveet-keikalle",
          }
        : isEnglishSetlistAppRoute
          ? {
              title: "Setlist App for Musicians & Bands | Paranoidi",
              description: "Build setlists from your song library, open lyrics and chords on stage and use reader mode and a prompter. Browser-based gig tools for live performers.",
              path: "/en/setlist-app-for-musicians",
            }
          : isOrderExtrasRoute
        ? {
            title: t("app.seo.orderExtras.title"),
            description: t("app.seo.orderExtras.description"),
            path: "/taydenna-tilaustasi",
            noindex: true,
          }
      : isContactRoute
        ? {
            title: t("app.seo.contact.title"),
            description: t("app.seo.contact.description"),
            path: "/yhteys",
            noindex: true,
          }
        : isPrivacyRoute
          ? {
              title: t("app.seo.privacy.title"),
              description: t("app.seo.privacy.description"),
              path: isEnglishPrivacyRoute ? "/en/privacy" : "/tietosuoja",
              noindex: true,
            }
          : isTermsRoute
            ? {
                title: t("app.seo.terms.title"),
                description: t("app.seo.terms.description"),
                path: isEnglishTermsRoute ? "/en/terms" : "/kayttoehdot",
                noindex: true,
              }
            : isLoginRoute
              ? {
                  title: t("app.seo.login.title"),
                  description: t("app.seo.login.description"),
                  path: "/login",
                  noindex: true,
                }
              : isResetPasswordRoute
                ? {
                    title: t("app.seo.resetPassword.title"),
                    description: t("app.seo.resetPassword.description"),
                    path: pathname,
                    noindex: true,
                  }
                : isAdminRoute
                  ? {
                      title: t("app.seo.admin.title"),
                      description: t("app.seo.admin.description"),
                      path: pathname,
                      noindex: true,
                    }
                  : isPublicArtistRoute
                    ? {
                        title: t("app.seo.publicArtist.title"),
                        description: t("app.seo.publicArtist.description"),
                        path: pathname,
                      }
                  : {
                    title: t("app.seo.notFound.title"),
                    description: t("app.seo.notFound.description"),
                    path: pathname,
                    noindex: true,
                  };

  // Hallintaan ei päästä browser storagen perusteella.
  // Backend vahvistaa kirjautumisen HttpOnly-session cookiesta.
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Storagea voidaan käyttää alustavana UI-datana,
  // mutta se ei itsessään anna käyttöoikeutta.
  const [loggedInPerformer, setLoggedInPerformer] = useState(() => {
    return getStoredPerformer();
  });

  const [isAuthChecking, setIsAuthChecking] = useState(true);

  const [loginName, setLoginName] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function validateAuthSession() {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/auth/session`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const data = await response.json().catch(() => null);

        if (cancelled) {
          return;
        }

        if (
          !response.ok ||
          !data?.ok ||
          !data?.user ||
          !data?.performer
        ) {
          clearStoredAuthState();
          setLoggedInPerformer(null);
          setIsLoggedIn(false);
          return;
        }

        setStoredValue(
          "paranoidi_auth_user",
          JSON.stringify(data.user)
        );

        setStoredValue(
          "paranoidi_auth_performer",
          JSON.stringify(data.performer)
        );

        setStoredValue(
          "paranoidi_performer_slug",
          data.performer.slug || ""
        );

        setStoredValue(
          "paranoidi_auth_type",
          data.auth?.type || "session"
        );

        setStoredValue(
          "paranoidi_logged_in",
          "true"
        );

        setLoggedInPerformer(data.performer);
        setIsLoggedIn(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error(
          "Kirjautumissession tarkistus epäonnistui:",
          error
        );

        clearStoredAuthState();
        setLoggedInPerformer(null);
        setIsLoggedIn(false);
      } finally {
        if (!cancelled) {
          setIsAuthChecking(false);
        }
      }
    }

    validateAuthSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const performerSlug = String(loggedInPerformer?.slug || "").trim();

    // Heartbeat kertoo backendille vain sen, että kirjautunut esiintyjä
    // käyttää Hallintaa parhaillaan. Taustalla oleva välilehti ei pidä
    // esiintyjää keinotekoisesti aktiivisena.
    if (!isLoggedIn || !isAdminRoute || !performerSlug) {
      return undefined;
    }

    let cancelled = false;
    let intervalId;

    async function sendActivityHeartbeat() {
      if (
        cancelled ||
        (typeof document !== "undefined" &&
          document.visibilityState !== "visible")
      ) {
        return;
      }

      try {
        await fetch(
          `${getApiBaseUrl()}/admin/performers/${encodeURIComponent(
            performerSlug
          )}/activity`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );
      } catch (error) {
        // Heartbeat ei ole Hallinnan kriittinen toiminto.
        // Mahdollinen verkkovirhe saa jäädä seuraavan sykkeen korjattavaksi.
        console.warn("Paranoidi activity heartbeat failed:", error);
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        sendActivityHeartbeat();
      }
    }

    sendActivityHeartbeat();

    intervalId = window.setInterval(() => {
      sendActivityHeartbeat();
    }, 60 * 1000);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;

      if (intervalId) {
        window.clearInterval(intervalId);
      }

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [isAdminRoute, isLoggedIn, loggedInPerformer?.slug]);

  async function handleLogin(event) {
    event.preventDefault();

    const identifier = loginName.trim();
    const password = loginPassword;

    if (!identifier || !password) {
      setLoginError(t("app.login.required"));
      return;
    }

    setIsLoggingIn(true);
    setLoginError("");

    try {
      const apiBaseUrl = getApiBaseUrl();

      const response = await fetch(apiBaseUrl + "/auth/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(t("app.login.failed"));
      }

      /*
        Tallennetaan backendistä tullut esiintyjä sessionStorageen.
        Täältä saadaan mm.:
        - performer.name
        - performer.plan
        - performer.features.setlistsEnabled
        - performer.features.songLimit

        Tässä ei päivitetä Reactin isLoggedIn/loggedInPerformer-tilaa,
        koska se ehtisi piirtää Hallinta-sivun ennen transition-loaderia.
        Uusi /admin-lataus lukee tiedot sessionStoragesta loaderin jälkeen.
      */
      if (data.performer) {
        setStoredValue(
          "paranoidi_auth_performer",
          JSON.stringify(data.performer)
        );

        setStoredValue(
          "paranoidi_performer_slug",
          data.performer?.slug || ""
        );
      } else {
        setStoredValue("paranoidi_auth_performer", "");
        setStoredValue("paranoidi_performer_slug", "");
      }

      /*
        Halutessa käyttäjätiedot ovat myöhemmin käytettävissä esim.
        käyttäjävalikkoon tai oikeuksien tarkistuksiin.
      */
      if (data.user) {
        setStoredValue("paranoidi_auth_user", JSON.stringify(data.user));
      } else {
        setStoredValue("paranoidi_auth_user", "");
      }

      setStoredValue("paranoidi_auth_type", "password");
      setStoredValue("paranoidi_logged_in", "true");

      window.sessionStorage.setItem("paranoidi_logged_in", "true");

      trackEvent("login_success", {
        performer_slug: data.performer?.slug || "",
        plan: data.performer?.plan || "",
      });

      window.sessionStorage.setItem("paranoidi_transition_loader", "1");
      window.location.replace("/admin");
      return;
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setIsLoggingIn(false);
    }
  }

  /*
    Lopullinen config, joka annetaan alas admin- ja public-sivuille.
    Basic on fallback, mutta kirjautuneella käyttäjällä käytetään backendin features-tietoa.
  */
  const appConfig = {
    ...baseConfig,
    artistName: loggedInPerformer?.name || "Paranoidi",
    performer: loggedInPerformer,
    features: loggedInPerformer?.features || BASIC_FEATURES,
  };

  const subscriptionStatus = String(
    loggedInPerformer?.subscriptionStatus ||
      loggedInPerformer?.subscription_status ||
      ""
  ).toLowerCase();

  const trialEndsAtRaw =
    loggedInPerformer?.trialEndsAt || loggedInPerformer?.trial_ends_at;

  const trialEndsAtTime = trialEndsAtRaw
    ? new Date(trialEndsAtRaw).getTime()
    : Number.NaN;

  const isTrialExpired =
    subscriptionStatus === "trialing" &&
    Number.isFinite(trialEndsAtTime) &&
    trialEndsAtTime <= Date.now();

  const isTrialActive =
    subscriptionStatus === "trialing" &&
    Number.isFinite(trialEndsAtTime) &&
    trialEndsAtTime > Date.now();

  const isSubscriptionActive = subscriptionStatus === "active";

  const isSubscriptionAccessBlocked =
    Boolean(loggedInPerformer) &&
    !isSubscriptionActive &&
    !isTrialActive;

  const subscriptionBlockedAdminView = (
    <main className="min-h-screen bg-black px-4 py-10 text-white sm:px-6">
      <div className="mx-auto flex min-h-[80vh] max-w-3xl items-center justify-center">
        <section className="w-full rounded-[2rem] border border-red-400/20 bg-zinc-950/90 p-8 text-center shadow-2xl shadow-red-950/30 md:p-10">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-red-300">
            Paranoidi
          </p>

          <h1 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">
            {isTrialExpired
              ? t("app.subscription.trialExpiredTitle")
              : t("app.subscription.inactiveTitle")}
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-zinc-300 md:text-lg">
            {isTrialExpired
              ? t("app.subscription.trialExpiredText")
              : t("app.subscription.inactiveText")}
          </p>

          <a
            href="/admin/subscription"
            className="mt-8 inline-flex rounded-full bg-red-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400"
          >
            {t("app.subscription.viewPlans")}
          </a>
        </section>
      </div>
    </main>
  );

  const isAuthSensitiveRoute =
    isLoginRoute ||
    isAdminRoute ||
    isContactRoute ||
    isOrderExtrasRoute;

  if (isAuthChecking && isAuthSensitiveRoute) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-black px-4 text-white">
        <div className="text-center">
          <div
            className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-red-400"
            aria-hidden="true"
          />

          <p className="mt-4 text-sm font-black uppercase tracking-[0.14em] text-zinc-300">
            {t("app.auth.checking")}
          </p>
        </div>
      </main>
    );
  }

  const loginView = (
    <LoginPage
      isLoggedIn={isLoggedIn}
      loggedInPerformer={loggedInPerformer}
      loginName={loginName}
      setLoginName={setLoginName}
      loginPassword={loginPassword}
      setLoginPassword={setLoginPassword}
      loginError={loginError}
      isLoggingIn={isLoggingIn}
      handleLogin={handleLogin}
    />
  );

  return (
    <ThemeProvider config={appConfig}>
      <>
         <Seo {...seoProps} />

        {isHomeRoute ? (
          <WaitlistPage />
        ) : isLoginRoute ? (
          loginView
        ) : isResetPasswordRoute ? (
          <ResetPasswordPage />
        ) : isContactRoute ? (
          !isLoggedIn ? (
            loginView
          ) : (
            <>
              <Navbar config={appConfig} />

              <div className="pt-[68px]">
                <ContactPage />
              </div>
            </>
          )
        ) : isPrivacyRoute ? (
          <PrivacyPage />
        ) : isTermsRoute ? (
          <TermsPage />
               ) : isFeaturesRoute ? (
          <FeaturesPage />
        ) : isSongRequestsLandingRoute ? (
          <SongRequestsLandingPage />
        ) : isEnglishSetlistAppRoute ? (
          <SetlistAppLandingPage />
        ) : isOrderExtrasRoute ? (
          !isLoggedIn ? (
            loginView
          ) : (
            <>
              <Navbar config={appConfig} />

              <div className="pt-[68px]">
                <OrderExtrasPage
                  isLoggedIn={isLoggedIn}
                  performer={loggedInPerformer}
                />
              </div>
            </>
          )
        ) : isPublicArtistRoute ? (
          <PublicArtistPage artistSlug={artistSlug} config={appConfig} />
        ) : isAdminRoute ? (
          !isLoggedIn ? (
            loginView
          ) : isAdminSubscriptionRoute ? (
            <SubscriptionPage config={appConfig} />
          ) : isSubscriptionAccessBlocked ? (
            subscriptionBlockedAdminView
          ) : isAdminSettingsRoute ? (
            <SettingsPage config={appConfig} />
          ) : isAdminFaqRoute ? (
            <AdminFaqPage config={appConfig} />
          ) : (
            <SongRequestsPage config={appConfig} />
          )
        ) : (
          <NotFoundPage />
        )}

        {!isAdminRoute && !isContactRoute && (
          isOrderExtrasRoute && isLoggedIn ? (
            <ParanoidiAdminFooter />
          ) : (
            <ParanoidiFooter
              showLoginLinks={!isPublicArtistRoute && !isLoggedIn}
              forceDark
            />
          )
        )}

        {!isAdminRoute && !isContactRoute && <ScrollToTopButton />}
        <CookieConsent />
      </>
    </ThemeProvider>
  );
}
